import { supabase } from '../../lib/supabase';
import { handleServiceError } from '../utils/serviceUtils';
import {
  DEFAULT_BUSINESS_HOURS_END,
  DEFAULT_BUSINESS_HOURS_START,
  DEFAULT_FOLLOW_UP_TIMEZONE,
  DEFAULT_MAX_SENDS_PER_CYCLE,
  DEFAULT_MIN_INTERVAL_SECONDS,
  DEFAULT_PAUSE_TIMEOUT_HOURS,
  FOLLOW_UP_CANCEL_REASON_CLOSED,
  FOLLOW_UP_CANCEL_REASON_OUT_OF_STAGE,
  clampMaxSendsPerCycle,
  clampMinIntervalSeconds,
  isFollowUpStageAllowed,
  formatDelayLabel,
  isAllowedFollowUpTimezone,
  isBusinessHoursRangeValid,
  sliceTimeToHHmm,
} from '../../features/settings/follow-up/followUp.constants';
import type {
  FollowUpDelayUnit,
  FollowUpJob,
  FollowUpJobStatus,
  FollowUpSettings,
  FollowUpTemplate,
} from '../../features/settings/follow-up/types';
import type { Database } from '../../src/types/database.types';

type FollowupTemplateRow = Database['public']['Tables']['followup_templates']['Row'];
type FollowupSettingsRow = Database['public']['Tables']['followup_settings']['Row'];
type FollowupJobRow = Database['public']['Tables']['followup_jobs']['Row'];

type FollowupJobWithTemplate = FollowupJobRow & {
  followup_templates: Pick<FollowupTemplateRow, 'delay_value' | 'delay_unit'> | null;
  opportunities: {
    title: string;
    contacts: { name: string; whatsapp: string | null; phone: string | null } | null;
  } | null;
  chat_conversations: {
    contact_name: string | null;
    contact_phone: string | null;
    whatsapp_phone: string | null;
  } | null;
};

function mapTemplateFromDB(row: FollowupTemplateRow): FollowUpTemplate {
  return {
    id: row.id,
    organizationId: row.organization_id,
    position: row.position,
    delayValue: row.delay_value,
    delayUnit: row.delay_unit as FollowUpDelayUnit,
    message: row.message,
    mediaUrl: row.media_url,
    aiPersonalize: !!row.ai_personalize,
    isActive: row.is_active !== false,
  };
}

async function cancelOpenJobsForTemplate(
  organizationId: string,
  templateId: string,
  reason: string,
) {
  const { error } = await supabase
    .from('followup_jobs')
    .update({ status: 'cancelled', failure_reason: reason })
    .eq('organization_id', organizationId)
    .eq('template_id', templateId)
    .in('status', ['scheduled', 'paused']);
  if (error) throw error;
}

function mapSettingsFromDB(row: FollowupSettingsRow): FollowUpSettings {
  return {
    organizationId: row.organization_id,
    backupConnectionIds: row.backup_connection_ids ?? [],
    minIntervalSeconds: clampMinIntervalSeconds(row.min_interval_seconds),
    maxSendsPerCycle: clampMaxSendsPerCycle(row.max_sends_per_cycle),
    pauseTimeoutHours: row.pause_timeout_hours ?? DEFAULT_PAUSE_TIMEOUT_HOURS,
    timezone: row.timezone ?? DEFAULT_FOLLOW_UP_TIMEZONE,
    businessHoursStart: sliceTimeToHHmm(row.business_hours_start ?? DEFAULT_BUSINESS_HOURS_START),
    businessHoursEnd: sliceTimeToHHmm(row.business_hours_end ?? DEFAULT_BUSINESS_HOURS_END),
    businessHoursWeekdaysOnly: row.business_hours_weekdays_only ?? true,
    stageIds: row.stage_ids ?? [],
  };
}

function createDefaultSettings(organizationId: string): FollowUpSettings {
  return {
    organizationId,
    backupConnectionIds: [],
    minIntervalSeconds: DEFAULT_MIN_INTERVAL_SECONDS,
    maxSendsPerCycle: DEFAULT_MAX_SENDS_PER_CYCLE,
    pauseTimeoutHours: DEFAULT_PAUSE_TIMEOUT_HOURS,
    timezone: DEFAULT_FOLLOW_UP_TIMEZONE,
    businessHoursStart: DEFAULT_BUSINESS_HOURS_START,
    businessHoursEnd: DEFAULT_BUSINESS_HOURS_END,
    businessHoursWeekdaysOnly: true,
    stageIds: [],
  };
}

type FollowUpJobDateColumn = 'scheduled_at' | 'sent_at' | 'pause_until';

function getDateColumnForStatus(status: FollowUpJobStatus): FollowUpJobDateColumn {
  if (status === 'sent') return 'sent_at';
  if (status === 'paused') return 'pause_until';
  return 'scheduled_at';
}

function mapJobFromDB(row: FollowupJobWithTemplate): FollowUpJob {
  const delayValue = row.followup_templates?.delay_value;
  const delayUnit = row.followup_templates?.delay_unit as FollowUpDelayUnit | undefined;
  const contactFromDeal = row.opportunities?.contacts;
  const conversation = row.chat_conversations;

  const contactName =
    contactFromDeal?.name?.trim() ||
    conversation?.contact_name?.trim() ||
    null;

  const contactPhone =
    row.contact_phone ||
    contactFromDeal?.whatsapp ||
    contactFromDeal?.phone ||
    conversation?.contact_phone ||
    conversation?.whatsapp_phone ||
    null;

  return {
    id: row.id,
    organizationId: row.organization_id,
    opportunityId: row.opportunity_id,
    conversationId: row.conversation_id,
    templateId: row.template_id,
    contactPhone,
    contactName,
    opportunityTitle: row.opportunities?.title?.trim() || null,
    scheduledAt: row.scheduled_at,
    sentAt: row.sent_at,
    status: row.status as FollowUpJobStatus,
    failureReason: row.failure_reason,
    connectionId: row.connection_id,
    pauseUntil: row.pause_until ?? null,
    delayLabel:
      delayValue != null && delayUnit
        ? formatDelayLabel(delayValue, delayUnit)
        : undefined,
  };
}

export async function listTemplates(organizationId: string): Promise<FollowUpTemplate[]> {
  try {
    const { data, error } = await supabase
      .from('followup_templates')
      .select('*')
      .eq('organization_id', organizationId)
      .order('position', { ascending: true });

    if (error) throw error;
    return (data ?? []).map(mapTemplateFromDB);
  } catch (error) {
    handleServiceError('listar templates de follow-up', error);
  }
}

export async function createTemplate(
  organizationId: string,
  template: Pick<FollowUpTemplate, 'position' | 'delayValue' | 'delayUnit' | 'message'> & {
    mediaUrl?: string | null;
    aiPersonalize?: boolean;
  },
): Promise<FollowUpTemplate> {
  try {
    const { data, error } = await supabase
      .from('followup_templates')
      .insert({
        organization_id: organizationId,
        position: template.position,
        delay_value: template.delayValue,
        delay_unit: template.delayUnit,
        message: template.message,
        media_url: template.mediaUrl ?? null,
        ai_personalize: !!template.aiPersonalize,
        is_active: true,
      })
      .select('*')
      .single();

    if (error) throw error;
    return mapTemplateFromDB(data);
  } catch (error) {
    handleServiceError('criar template de follow-up', error);
  }
}

export async function updateTemplate(
  organizationId: string,
  templateId: string,
  patch: Partial<Pick<FollowUpTemplate, 'position' | 'delayValue' | 'delayUnit' | 'message' | 'mediaUrl' | 'aiPersonalize' | 'isActive'>>,
): Promise<FollowUpTemplate> {
  try {
    const dbPatch: Database['public']['Tables']['followup_templates']['Update'] = {};
    if (patch.position !== undefined) dbPatch.position = patch.position;
    if (patch.delayValue !== undefined) dbPatch.delay_value = patch.delayValue;
    if (patch.delayUnit !== undefined) dbPatch.delay_unit = patch.delayUnit;
    if (patch.message !== undefined) dbPatch.message = patch.message;
    if (patch.mediaUrl !== undefined) dbPatch.media_url = patch.mediaUrl;
    if (patch.aiPersonalize !== undefined) dbPatch.ai_personalize = patch.aiPersonalize;
    if (patch.isActive !== undefined) dbPatch.is_active = patch.isActive;
    if (patch.isActive === false) {
      await cancelOpenJobsForTemplate(organizationId, templateId, 'Follow-up desativado');
    }

    const { data, error } = await supabase
      .from('followup_templates')
      .update(dbPatch)
      .eq('organization_id', organizationId)
      .eq('id', templateId)
      .select('*')
      .single();

    if (error) throw error;
    return mapTemplateFromDB(data);
  } catch (error) {
    handleServiceError('atualizar template de follow-up', error);
  }
}

export async function deleteTemplate(organizationId: string, templateId: string): Promise<void> {
  try {
    await cancelOpenJobsForTemplate(organizationId, templateId, 'Template removido');

    const { error } = await supabase
      .from('followup_templates')
      .delete()
      .eq('organization_id', organizationId)
      .eq('id', templateId);

    if (error) throw error;
  } catch (error) {
    handleServiceError('apagar template de follow-up', error);
  }
}

export async function getSettings(organizationId: string): Promise<FollowUpSettings> {
  try {
    const { data, error } = await supabase
      .from('followup_settings')
      .select('*')
      .eq('organization_id', organizationId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return createDefaultSettings(organizationId);

    return mapSettingsFromDB(data);
  } catch (error) {
    handleServiceError('buscar configurações de follow-up', error);
  }
}

export async function upsertSettings(
  organizationId: string,
  patch: Partial<Omit<FollowUpSettings, 'organizationId'>>
): Promise<FollowUpSettings> {
  try {
    const dbPatch: Database['public']['Tables']['followup_settings']['Insert'] = {
      organization_id: organizationId,
    };

    if (patch.backupConnectionIds !== undefined) {
      dbPatch.backup_connection_ids = patch.backupConnectionIds;
    }
    if (patch.minIntervalSeconds !== undefined) {
      dbPatch.min_interval_seconds = clampMinIntervalSeconds(patch.minIntervalSeconds);
    }
    if (patch.maxSendsPerCycle !== undefined) {
      dbPatch.max_sends_per_cycle = clampMaxSendsPerCycle(patch.maxSendsPerCycle);
    }
    if (patch.pauseTimeoutHours !== undefined) {
      dbPatch.pause_timeout_hours = Math.max(1, patch.pauseTimeoutHours);
    }

    const touchesBusinessHours =
      patch.timezone !== undefined ||
      patch.businessHoursStart !== undefined ||
      patch.businessHoursEnd !== undefined ||
      patch.businessHoursWeekdaysOnly !== undefined;

    let existing: FollowUpSettings | null = null;
    if (
      touchesBusinessHours &&
      (patch.businessHoursStart === undefined || patch.businessHoursEnd === undefined)
    ) {
      existing = await getSettings(organizationId);
    }

    if (patch.timezone !== undefined) {
      if (!isAllowedFollowUpTimezone(patch.timezone)) {
        throw new Error('Fuso horário inválido.');
      }
      dbPatch.timezone = patch.timezone;
    }

    if (patch.businessHoursStart !== undefined || patch.businessHoursEnd !== undefined) {
      const resolvedStart = sliceTimeToHHmm(
        patch.businessHoursStart ?? existing?.businessHoursStart ?? DEFAULT_BUSINESS_HOURS_START
      );
      const resolvedEnd = sliceTimeToHHmm(
        patch.businessHoursEnd ?? existing?.businessHoursEnd ?? DEFAULT_BUSINESS_HOURS_END
      );

      if (!isBusinessHoursRangeValid(resolvedStart, resolvedEnd)) {
        throw new Error('O horário de início deve ser anterior ao horário de fim.');
      }

      if (patch.businessHoursStart !== undefined) {
        dbPatch.business_hours_start = resolvedStart;
      }
      if (patch.businessHoursEnd !== undefined) {
        dbPatch.business_hours_end = resolvedEnd;
      }
    }

    if (patch.businessHoursWeekdaysOnly !== undefined) {
      dbPatch.business_hours_weekdays_only = patch.businessHoursWeekdaysOnly;
    }
    if (patch.stageIds !== undefined) {
      dbPatch.stage_ids = [...new Set(patch.stageIds.filter(Boolean))];
    }

    const { data, error } = await supabase
      .from('followup_settings')
      .upsert(dbPatch, { onConflict: 'organization_id' })
      .select('*')
      .single();

    if (error) throw error;
    return mapSettingsFromDB(data);
  } catch (error) {
    handleServiceError('salvar configurações de follow-up', error);
  }
}

export async function listJobs(
  organizationId: string,
  filters: { status: FollowUpJobStatus; startDate?: string; endDate?: string }
): Promise<FollowUpJob[]> {
  try {
    const dateColumn = getDateColumnForStatus(filters.status);

    let query = supabase
      .from('followup_jobs')
      .select(`
        *,
        followup_templates (
          delay_value,
          delay_unit
        ),
        opportunities (
          title,
          contacts (
            name,
            whatsapp,
            phone
          )
        ),
        chat_conversations (
          contact_name,
          contact_phone,
          whatsapp_phone
        )
      `)
      .eq('organization_id', organizationId)
      .eq('status', filters.status)
      .order(dateColumn, { ascending: false });

    if (filters.startDate) {
      query = query.gte(dateColumn, filters.startDate);
    }
    if (filters.endDate) {
      query = query.lte(dateColumn, filters.endDate);
    }

    const { data, error } = await query;

    if (error) throw error;
    return ((data ?? []) as FollowupJobWithTemplate[]).map(mapJobFromDB);
  } catch (error) {
    handleServiceError('listar jobs de follow-up', error);
  }
}

/**
 * Cancela jobs ativos se a negociação fechou (ganho/perdido) ou saiu das etapas
 * permitidas. Idempotente — 0 linhas se já não houver jobs `scheduled`/`paused`.
 */
export async function syncFollowUpsAfterLeadChange(
  opportunityId: string,
  next: { status?: string | null; stage?: string | null }
): Promise<number> {
  const status = next.status ?? 'open';
  if (status === 'won' || status === 'lost') {
    return cancelFollowUpsForOpportunity(opportunityId, FOLLOW_UP_CANCEL_REASON_CLOSED);
  }

  if (next.stage === undefined) return 0;

  const { data: opportunity, error } = await supabase
    .from('opportunities')
    .select('organization_id')
    .eq('id', opportunityId)
    .maybeSingle();

  if (error || !opportunity?.organization_id) return 0;

  const settings = await getSettings(opportunity.organization_id);
  if (isFollowUpStageAllowed(next.stage, settings.stageIds)) return 0;

  return cancelFollowUpsForOpportunity(opportunityId, FOLLOW_UP_CANCEL_REASON_OUT_OF_STAGE);
}

/**
 * Cancela jobs `scheduled` de uma oportunidade (ex.: ao marcar como ganha/perdida).
 * Chamado por `WinDealUseCase`/`LoseDealUseCase`/`UpdateLeadUseCase`; o cron de
 * envio também revalida `status` e etapa antes de disparar.
 */
export async function cancelFollowUpsForOpportunity(
  opportunityId: string,
  reason: string = FOLLOW_UP_CANCEL_REASON_CLOSED
): Promise<number> {
  try {
    // RPC (SECURITY INVOKER + followup_jobs UPDATE policy for org members).
    const { data, error } = await supabase.rpc('cancel_followup_jobs_for_opportunity', {
      p_opportunity_id: opportunityId,
      p_reason: reason,
    });

    if (error) throw error;
    return data ?? 0;
  } catch (error) {
    handleServiceError('cancelar follow-ups da negociação', error);
  }
}

type FollowUpJobCountStatus = 'scheduled' | 'sent' | 'failed' | 'paused';

export async function countJobsByStatus(
  organizationId: string,
  filters?: { startDate?: string; endDate?: string }
): Promise<Record<FollowUpJobCountStatus, number>> {
  try {
    const statuses: FollowUpJobCountStatus[] = ['scheduled', 'sent', 'failed', 'paused'];
    const hasDateFilter = Boolean(filters?.startDate || filters?.endDate);

    if (!hasDateFilter) {
      const { data, error } = await supabase
        .from('followup_jobs')
        .select('status')
        .eq('organization_id', organizationId)
        .in('status', statuses);

      if (error) throw error;

      const counts: Record<FollowUpJobCountStatus, number> = {
        scheduled: 0,
        sent: 0,
        failed: 0,
        paused: 0,
      };

      for (const row of data ?? []) {
        const status = row.status as keyof typeof counts;
        if (status in counts) {
          counts[status] += 1;
        }
      }

      return counts;
    }

    const countResults = await Promise.all(
      statuses.map(async (status) => {
        const dateColumn = getDateColumnForStatus(status);

        let query = supabase
          .from('followup_jobs')
          .select('*', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .eq('status', status);

        if (filters?.startDate) {
          query = query.gte(dateColumn, filters.startDate);
        }
        if (filters?.endDate) {
          query = query.lte(dateColumn, filters.endDate);
        }

        const { count, error } = await query;
        if (error) throw error;

        return [status, count ?? 0] as const;
      })
    );

    return Object.fromEntries(countResults) as Record<FollowUpJobCountStatus, number>;
  } catch (error) {
    handleServiceError('contar jobs de follow-up por status', error);
  }
}
